"use client";

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import React, { useCallback, useMemo, useSyncExternalStore } from "react";
import { AnimatePresence, LazyMotion, domAnimation, m } from "framer-motion";
import BrainMobileTabs from "./mobile/BrainMobileTabs";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import BrainDesktopDrop from "./BrainDesktopDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useWaveData } from "@/hooks/useWaveData";
import { useWaveTimers } from "@/hooks/useWaveTimers";
import { useWave } from "@/hooks/useWave";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import useDeviceInfo from "@/hooks/useDeviceInfo";
import MemesQuickVoteDialog from "./left-sidebar/waves/memes-quick-vote/MemesQuickVoteDialog";
import { getActiveWaveIdFromUrl, getHomeRoute, getWaveHomeRoute } from "@/helpers/navigation.helpers";
import { markDropCloseNavigation } from "@/helpers/drop-close-navigation.helpers";
import CreateWaveModal from "@/components/waves/create-wave/CreateWaveModal";
import CreateDirectMessageModal from "@/components/waves/create-dm/CreateDirectMessageModal";
import { useAuth } from "@/components/auth/Auth";
import { useMyStreamOptional } from "@/contexts/wave/MyStreamContext";
import { useClosingDropId } from "@/hooks/useClosingDropId";
import { useMemesQuickVoteDialogController } from "@/hooks/useMemesQuickVoteDialogController";
import BrainMobileViewContent from "./mobile/BrainMobileViewContent";
import { BrainView } from "./mobile/brainMobileViews";
import { useBrainMobileActiveView } from "./mobile/useBrainMobileActiveView";
import { DROP_DETAIL_STALE_TIME_MS, fetchDropByIdBatched, getDropQueryKey } from "@/services/api/drop-api";
interface Props {
  readonly children: ReactNode;
}
const BrainMobile: React.FC<Props> = t0 => {
  const $ = _c(98);
  const {
    children
  } = t0;
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const {
    isApp
  } = useDeviceInfo();
  const {
    connectedProfile
  } = useAuth();
  const hasAuthenticatedProfile = Boolean(connectedProfile?.handle);
  const quickVote = useMemesQuickVoteDialogController();
  const hydrated = useSyncExternalStore(_temp2, _temp3, _temp4);
  const myStream = useMyStreamOptional();
  let t1;
  if ($[0] !== searchParams) {
    t1 = searchParams.get("drop") ?? undefined;
    $[0] = searchParams;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const dropId = t1;
  const {
    effectiveDropId,
    beginClosingDrop
  } = useClosingDropId(dropId);
  let t2;
  if ($[2] !== effectiveDropId) {
    t2 = getDropQueryKey(effectiveDropId);
    $[2] = effectiveDropId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== effectiveDropId) {
    t3 = () => {
      if (!effectiveDropId) {
        throw new Error("Cannot fetch drop without a drop id");
      }
      return fetchDropByIdBatched(effectiveDropId);
    };
    $[4] = effectiveDropId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!effectiveDropId;
  let t5;
  if ($[6] !== t2 || $[7] !== t3 || $[8] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      placeholderData: keepPreviousData,
      enabled: t4,
      staleTime: DROP_DETAIL_STALE_TIME_MS
    };
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const {
    data: drop
  } = useQuery(t5);
  let t6;
  if ($[10] !== myStream?.activeWave.id || $[11] !== pathname || $[12] !== searchParams) {
    t6 = myStream?.activeWave.id ?? getActiveWaveIdFromUrl({
      pathname,
      searchParams
    }) ?? null;
    $[10] = myStream?.activeWave.id;
    $[11] = pathname;
    $[12] = searchParams;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const waveId = t6;
  let t7;
  if ($[14] !== isApp || $[15] !== pathname || $[16] !== router || $[17] !== searchParams) {
    t7 = () => {
      const params = new URLSearchParams(searchParams.toString() || "");
      params.delete("wave");
      const basePath = getWaveHomeRoute({
        isDirectMessage: pathname.startsWith("/messages"),
        isApp
      });
      const newUrl = params.toString() ? `${basePath}?${params.toString()}` : basePath || getHomeRoute();
      router.push(newUrl, {
        scroll: false
      });
    };
    $[14] = isApp;
    $[15] = pathname;
    $[16] = router;
    $[17] = searchParams;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7 || $[20] !== waveId) {
    t8 = {
      waveId,
      onWaveNotFound: t7
    };
    $[19] = t7;
    $[20] = waveId;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const {
    data: wave
  } = useWaveData(t8);
  const {
    isMemesWave,
    isCurationWave,
    isRankWave,
    isApproveWave
  } = useWave(wave);
  const {
    voting: t9,
    decisions: t10
  } = useWaveTimers(wave);
  const {
    isCompleted
  } = t9;
  const {
    firstDecisionDone
  } = t10;
  let t11;
  if ($[22] !== firstDecisionDone || $[23] !== hasAuthenticatedProfile || $[24] !== isApp || $[25] !== isApproveWave || $[26] !== isCompleted || $[27] !== isCurationWave || $[28] !== isMemesWave || $[29] !== isRankWave || $[30] !== pathname || $[31] !== searchParams || $[32] !== wave || $[33] !== waveId) {
    t11 = {
      firstDecisionDone,
      isApp,
      isCompleted,
      hasAuthenticatedProfile,
      isCurationWave,
      isMemesWave,
      isRankWave,
      isApproveWave,
      pathname,
      searchParams,
      wave,
      waveId
    };
    $[22] = firstDecisionDone;
    $[23] = hasAuthenticatedProfile;
    $[24] = isApp;
    $[25] = isApproveWave;
    $[26] = isCompleted;
    $[27] = isCurationWave;
    $[28] = isMemesWave;
    $[29] = isRankWave;
    $[30] = pathname;
    $[31] = searchParams;
    $[32] = wave;
    $[33] = waveId;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  const {
    activeView,
    onViewChange
  } = useBrainMobileActiveView(t11);
  let t12;
  if ($[35] !== pathname || $[36] !== router || $[37] !== searchParams) {
    t12 = selectedDrop => {
      const params_0 = new URLSearchParams(searchParams.toString() || "");
      params_0.set("drop", selectedDrop.id);
      router.push(`${pathname}?${params_0.toString()}`, {
        scroll: false
      });
    };
    $[35] = pathname;
    $[36] = router;
    $[37] = searchParams;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  const onDropClick = t12;
  let t13;
  if ($[39] !== beginClosingDrop || $[40] !== dropId || $[41] !== pathname || $[42] !== router || $[43] !== searchParams) {
    t13 = () => {
      if (dropId) {
        beginClosingDrop(dropId);
      }
      markDropCloseNavigation();
      const params_1 = new URLSearchParams(searchParams.toString() || "");
      params_1.delete("drop");
      const newUrl_0 = params_1.toString() ? `${pathname}?${params_1.toString()}` : pathname || getHomeRoute();
      router.replace(newUrl_0, {
        scroll: false
      });
    };
    $[39] = beginClosingDrop;
    $[40] = dropId;
    $[41] = pathname;
    $[42] = router;
    $[43] = searchParams;
    $[44] = t13;
  } else {
    t13 = $[44];
  }
  const onDropClose = t13;
  let t14;
  if ($[45] !== drop || $[46] !== effectiveDropId) {
    t14 = !!effectiveDropId && !!drop && drop.id.toLowerCase() === effectiveDropId.toLowerCase();
    $[45] = drop;
    $[46] = effectiveDropId;
    $[47] = t14;
  } else {
    t14 = $[47];
  }
  const isDropOpen = t14;
  const hasWave = Boolean(waveId);
  let t15;
  if ($[48] !== pathname || $[49] !== router || $[50] !== searchParams) {
    t15 = () => {
      const params_2 = new URLSearchParams(searchParams.toString() || "");
      params_2.delete("create");
      const base = pathname;
      const next = params_2.toString() ? `${base}?${params_2.toString()}` : base;
      router.replace(next, {
        scroll: false
      });
    };
    $[48] = pathname;
    $[49] = router;
    $[50] = searchParams;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  const closeCreateOverlay = t15;
  let t16;
  bb0: {
    if (!isApp) {
      t16 = null;
      break bb0;
    }
    const createParam = searchParams.get("create");
    if (!createParam) {
      t16 = null;
      break bb0;
    }
    if (!connectedProfile) {
      t16 = null;
      break bb0;
    }
    if (createParam === "dm") {
      let t17;
      if ($[52] !== closeCreateOverlay || $[53] !== connectedProfile) {
        t17 = <CreateDirectMessageModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[52] = closeCreateOverlay;
        $[53] = connectedProfile;
        $[54] = t17;
      } else {
        t17 = $[54];
      }
      t16 = t17;
      break bb0;
    }
    if (createParam === "wave") {
      let t17;
      if ($[55] !== closeCreateOverlay || $[56] !== connectedProfile) {
        t17 = <CreateWaveModal isOpen={true} onClose={closeCreateOverlay} profile={connectedProfile} />;
        $[55] = closeCreateOverlay;
        $[56] = connectedProfile;
        $[57] = t17;
      } else {
        t17 = $[57];
      }
      t16 = t17;
      break bb0;
    }
    t16 = null;
  }
  const createOverlay = t16;
  const shouldMountQuickVoteDialog = quickVote.isQuickVoteOpen || activeView === BrainView.WAVES;
  const dropOverlayClass = isApp ? "tw-fixed tw-inset-0 tw-z-[1010] tw-bg-black tailwind-scope" : "tw-absolute tw-inset-0 tw-z-[1010]";
  let t17;
  if ($[58] !== drop || $[59] !== dropOverlayClass || $[60] !== isDropOpen || $[61] !== onDropClose) {
    t17 = isDropOpen && <div className={dropOverlayClass}><BrainDesktopDrop drop={{
        type: DropSize.FULL,
        ...drop,
        stableKey: drop.id,
        stableHash: drop.id
      }} onClose={onDropClose} /></div>;
    $[58] = drop;
    $[59] = dropOverlayClass;
    $[60] = isDropOpen;
    $[61] = onDropClose;
    $[62] = t17;
  } else {
    t17 = $[62];
  }
  let t18;
  if ($[63] !== activeView || $[64] !== hasWave || $[65] !== hydrated || $[66] !== isApp || $[67] !== onViewChange || $[68] !== wave) {
    t18 = (hasWave || !isApp) && <BrainMobileTabs activeView={activeView} onViewChange={onViewChange} wave={wave} waveActive={hasWave} showWavesTab={hydrated} showStreamBack={hydrated} isApp={isApp} />;
    $[63] = activeView;
    $[64] = hasWave;
    $[65] = hydrated;
    $[66] = isApp;
    $[67] = onViewChange;
    $[68] = wave;
    $[69] = t18;
  } else {
    t18 = $[69];
  }
  let t19;
  let t20;
  let t21;
  let t22;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = {
      opacity: 0,
      x: 20
    };
    t20 = {
      opacity: 1,
      x: 0
    };
    t21 = {
      opacity: 0,
      x: -20
    };
    t22 = {
      duration: 0.2,
      ease: "easeInOut"
    };
    $[70] = t19;
    $[71] = t20;
    $[72] = t21;
    $[73] = t22;
  } else {
    t19 = $[70];
    t20 = $[71];
    t21 = $[72];
    t22 = $[73];
  }
  let t23;
  if ($[74] !== activeView || $[75] !== children || $[76] !== isApproveWave || $[77] !== isCurationWave || $[78] !== isMemesWave || $[79] !== isRankWave || $[80] !== onDropClick || $[81] !== quickVote.openQuickVote || $[82] !== quickVote.prefetchQuickVote || $[83] !== wave || $[84] !== waveId) {
    t23 = <BrainMobileViewContent activeView={activeView} activeWaveId={waveId} isCurationWave={isCurationWave} isMemesWave={isMemesWave} isRankWave={isRankWave} isApproveWave={isApproveWave} onDropClick={onDropClick} onOpenQuickVote={quickVote.openQuickVote} onPrefetchQuickVote={quickVote.prefetchQuickVote} wave={wave}>{children}</BrainMobileViewContent>;
    $[74] = activeView;
    $[75] = children;
    $[76] = isApproveWave;
    $[77] = isCurationWave;
    $[78] = isMemesWave;
    $[79] = isRankWave;
    $[80] = onDropClick;
    $[81] = quickVote.openQuickVote;
    $[82] = quickVote.prefetchQuickVote;
    $[83] = wave;
    $[84] = waveId;
    $[85] = t23;
  } else {
    t23 = $[85];
  }
  let t24;
  if ($[86] !== activeView || $[87] !== t23) {
    t24 = <LazyMotion features={domAnimation}><AnimatePresence mode="wait"><m.div key={activeView} initial={t19} animate={t20} exit={t21} transition={t22} className="tw-relative tw-min-w-0 tw-flex-1">{t23}</m.div></AnimatePresence></LazyMotion>;
    $[86] = activeView;
    $[87] = t23;
    $[88] = t24;
  } else {
    t24 = $[88];
  }
  let t25;
  if ($[89] !== quickVote.dialogState || $[90] !== shouldMountQuickVoteDialog) {
    t25 = shouldMountQuickVoteDialog && <MemesQuickVoteDialog {...quickVote.dialogState} />;
    $[89] = quickVote.dialogState;
    $[90] = shouldMountQuickVoteDialog;
    $[91] = t25;
  } else {
    t25 = $[91];
  }
  let t26;
  if ($[92] !== createOverlay || $[93] !== t17 || $[94] !== t18 || $[95] !== t24 || $[96] !== t25) {
    t26 = <div className="tw-relative tw-flex tw-h-full tw-flex-col">{createOverlay}{t17}{t18}{t24}{t25}</div>;
    $[92] = createOverlay;
    $[93] = t17;
    $[94] = t18;
    $[95] = t24;
    $[96] = t25;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  return t26;
};
export default BrainMobile;
function _temp() {}
function _temp2() {
  return _temp;
}
function _temp3() {
  return true;
}
function _temp4() {
  return false;
}